'use client';
import { useState, useRef, useEffect } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import Icon from '@/components/ui/AppIcon';
import ServicesMegaMenu from '@/components/common/ServicesMegaMenu';

interface DropdownItem {
  label: string;
  href: string;
}

interface NavDropdown {
  id: string;
  label: string;
  items: DropdownItem[];
}

const dropdowns: NavDropdown[] = [
  {
    id: 'how-it-works',
    label: 'How It Works',
    items: [
      { label: 'Sprint / Diagnostic', href: '/lp/strategy-sprint' },
      { label: 'Buildout / Project', href: '/lp/foundations' },
      { label: 'Ongoing Execution', href: '/lp/execution-coverage' },
      { label: 'DIY / Subscription', href: '/diy' },
    ],
  },
  {
    id: 'proof',
    label: 'Proof',
    items: [
      { label: 'Portfolio', href: '/portfolio' },
      { label: 'Testimonials', href: '/testimonials' },
      { label: 'Selected Results', href: '/case-studies' },
    ],
  },
  {
    id: 'about-daniel',
    label: 'About',
    items: [
      { label: 'Best Odds Corp', href: '/about' },
      { label: 'Meet Our Team', href: '/team' },
      { label: 'Work With Daniel', href: '/work-with-daniel' },
    ],
  },
];

const mobileServicesColumns = [
  {
    id: 'strategy-audits',
    title: 'Strategy & Audits',
    items: [
      { label: 'Growth Bottleneck Audit', href: '/services/strategy' },
      { label: 'Operational Strategy', href: '/services/operational-strategy-consultation' },
      { label: 'Market Research', href: '/services/market-research' },
      { label: 'Offer & Pricing Review', href: '/services/pricing-analysis' },
    ],
  },
  {
    id: 'growth-demand',
    title: 'Growth & Demand',
    items: [
      { label: 'Paid Search', href: '/services/paid-search' },
      { label: 'Paid Social', href: '/services/paid-social' },
      { label: 'SEO / GEO / AEO', href: '/services/seo' },
      { label: 'Funnel Strategy', href: '/lp/strategy-sprint' },
    ],
  },
  {
    id: 'systems-execution',
    title: 'Systems & Execution',
    items: [
      { label: 'AI Automation', href: '/services/internal-operational-structure' },
      { label: 'CRM / Follow-Up Systems', href: '/services/lifecycle-marketing' },
      { label: 'Content Systems', href: '/services/content' },
      { label: 'Lifecycle Marketing', href: '/services/lifecycle-marketing' },
    ],
  },
  {
    id: 'brand-web',
    title: 'Brand & Web',
    items: [
      { label: 'Website & Funnel Build', href: '/services/web-design' },
      { label: 'Branding & Creative', href: '/services/branding-design' },
      { label: 'Content Production', href: '/services/content' },
      { label: 'Pitch Decks', href: '/services/pitch-deck-presentation' },
    ],
  },
];

export default function Header() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [megaMenuOpen, setMegaMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [mobileAccordion, setMobileAccordion] = useState<string | null>(null);
  const servicesLinkRef = useRef<HTMLDivElement>(null);
  const megaMenuTimeoutRef = useRef<number | null>(null);
  const dropdownTimeoutRef = useRef<number | null>(null);

  const toggleMobileAccordion = (id: string) => {
    setMobileAccordion((prev) => (prev === id ? null : id));
  };

  const handleServicesMouseEnter = () => {
    if (megaMenuTimeoutRef?.current) clearTimeout(megaMenuTimeoutRef.current);
    setActiveDropdown(null);
    setMegaMenuOpen(true);
  };

  const handleServicesMouseLeave = () => {
    megaMenuTimeoutRef.current = setTimeout(() => {
      setMegaMenuOpen(false);
    }, 200);
  };

  const handleMegaMenuMouseEnter = () => {
    if (megaMenuTimeoutRef?.current) clearTimeout(megaMenuTimeoutRef.current);
  };

  const handleMegaMenuMouseLeave = () => {
    setMegaMenuOpen(false);
  };

  const handleDropdownMouseEnter = (id: string) => {
    if (dropdownTimeoutRef?.current) clearTimeout(dropdownTimeoutRef.current);
    setMegaMenuOpen(false);
    setActiveDropdown(id);
  };

  const handleDropdownMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setActiveDropdown(null);
    }, 200);
  };

  const handleDropdownMenuMouseEnter = () => {
    if (dropdownTimeoutRef?.current) clearTimeout(dropdownTimeoutRef.current);
  };

  const handleDropdownMenuMouseLeave = () => {
    setActiveDropdown(null);
  };

  useEffect(() => {
    return () => {
      if (megaMenuTimeoutRef?.current) clearTimeout(megaMenuTimeoutRef.current);
      if (dropdownTimeoutRef?.current) clearTimeout(dropdownTimeoutRef.current);
    };
  }, []);

  const closeMobileMenu = () => {
    setMobileMenuOpen(false);
    setMobileAccordion(null);
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/80 backdrop-blur-md border-b border-border">
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
        {/* Logo */}
        <Link href="/" className="flex-shrink-0">
          <AppImage
            src="/assets/images/boc_logo_240x90_white-1771394304209.png"
            alt="Best Odds Corp Logo"
            width={180}
            height={68}
            priority={true}
            className="cursor-pointer w-[120px] sm:w-[150px] lg:w-[180px] h-auto"
          />
        </Link>

        {/* Desktop Navigation */}
        <div className="hidden lg:flex items-center gap-6 xl:gap-8">
          {/* Services — mega menu */}
          <div
            ref={servicesLinkRef}
            className="relative"
            onMouseEnter={handleServicesMouseEnter}
            onMouseLeave={handleServicesMouseLeave}
          >
            <button
              onClick={() => setMegaMenuOpen(!megaMenuOpen)}
              className="text-[10px] uppercase tracking-ultra-wide text-muted-foreground hover:text-foreground transition-colors font-medium flex items-center gap-1"
            >
              Services
              <Icon
                name="ChevronDownIcon"
                size={14}
                className={`transition-transform duration-base ${megaMenuOpen ? 'rotate-180' : ''}`}
              />
            </button>
          </div>

          {/* Dropdowns: How It Works, Proof, About Daniel */}
          {dropdowns.map((dropdown) => (
            <div
              key={dropdown.id}
              className="relative"
              onMouseEnter={() => handleDropdownMouseEnter(dropdown.id)}
              onMouseLeave={handleDropdownMouseLeave}
            >
              <button
                className="text-[10px] uppercase tracking-ultra-wide text-muted-foreground hover:text-foreground transition-colors font-medium flex items-center gap-1 whitespace-nowrap"
              >
                {dropdown.label}
                <Icon
                  name="ChevronDownIcon"
                  size={14}
                  className={`transition-transform duration-base ${activeDropdown === dropdown.id ? 'rotate-180' : ''}`}
                />
              </button>

              {/* Dropdown Panel */}
              {activeDropdown === dropdown.id && (
                <div
                  className="absolute top-full left-0 mt-2 w-52 bg-surface-1 border border-border shadow-xl z-50 py-2"
                  onMouseEnter={handleDropdownMenuMouseEnter}
                  onMouseLeave={handleDropdownMenuMouseLeave}
                >
                  {dropdown.items.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={() => setActiveDropdown(null)}
                      className="block px-4 py-2.5 text-[10px] uppercase tracking-wider text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}

          {/* Pricing — direct link */}
          <Link
            href="/pricing"
            className="text-[10px] uppercase tracking-ultra-wide text-muted-foreground hover:text-foreground transition-colors font-medium"
          >
            Pricing
          </Link>

          {/* Website Rescue — direct link */}
          <Link
            href="/website-rescue"
            className="text-[10px] uppercase tracking-ultra-wide text-muted-foreground hover:text-primary transition-colors font-medium whitespace-nowrap"
          >
            Website Rescue
          </Link>

          {/* Book a Sprint CTA */}
          <Link
            href="/book"
            className="px-5 py-2 bg-primary text-primary-foreground text-[10px] uppercase tracking-widest hover:bg-primary/90 transition-colors font-semibold whitespace-nowrap"
            onClick={() => {
              if (typeof window !== 'undefined' && typeof (window as Window & { gtag?: Function }).gtag === 'function') {
                (window as Window & { gtag: Function }).gtag('event', 'cta_click', { event_category: 'header_nav', cta_label: 'Book a Sprint', location: 'desktop_nav' });
              }
            }}
          >
            Book a Sprint
          </Link>
        </div>

        {/* Mobile Menu Button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="lg:hidden p-2 text-foreground"
          aria-label="Toggle menu"
        >
          <Icon name={mobileMenuOpen ? 'XMarkIcon' : 'Bars3Icon'} size={24} />
        </button>
      </div>

      {/* Mega Menu Dropdown */}
      <div onMouseEnter={handleMegaMenuMouseEnter} onMouseLeave={handleMegaMenuMouseLeave}>
        <ServicesMegaMenu isOpen={megaMenuOpen} onClose={() => setMegaMenuOpen(false)} />
      </div>

      {/* Mobile Menu */}
      {mobileMenuOpen && (
        <div className="lg:hidden bg-card border-t border-border overflow-y-auto max-h-[calc(100vh-64px)]">
          <div className="px-5 py-5 flex flex-col">

            {/* Services Accordion */}
            <div className="border-b border-border/40">
              <button
                onClick={() => toggleMobileAccordion('services')}
                className="w-full flex items-center justify-between py-4 text-[11px] uppercase tracking-widest font-semibold text-card-foreground"
              >
                <span>Services</span>
                <Icon
                  name="ChevronDownIcon"
                  size={16}
                  className={`text-muted-foreground transition-transform duration-200 ${mobileAccordion === 'services' ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileAccordion === 'services' && (
                <div className="pb-4 space-y-5">
                  {mobileServicesColumns.map((col) => (
                    <div key={col.id}>
                      <p className="text-[9px] uppercase tracking-ultra-wide text-primary font-semibold mb-2 pl-1">
                        {col.title}
                      </p>
                      <div className="space-y-0.5">
                        {col.items.map((item) => (
                          <Link
                            key={item.href + item.label}
                            href={item.href}
                            onClick={closeMobileMenu}
                            className="block py-2.5 pl-3 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
                          >
                            {item.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ))}
                  <div className="pt-2 pl-1">
                    <Link
                      href="/services/menu"
                      onClick={closeMobileMenu}
                      className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-foreground hover:text-primary transition-colors font-semibold"
                    >
                      View All Services
                      <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                        <path strokeLinecap="round" strokeLinejoin="round" d="M13.5 4.5L21 12m0 0l-7.5 7.5M21 12H3" />
                      </svg>
                    </Link>
                  </div>
                </div>
              )}
            </div>

            {/* How It Works Accordion */}
            <div className="border-b border-border/40">
              <button
                onClick={() => toggleMobileAccordion('how-it-works')}
                className="w-full flex items-center justify-between py-4 text-[11px] uppercase tracking-widest font-semibold text-card-foreground"
              >
                <span>How It Works</span>
                <Icon
                  name="ChevronDownIcon"
                  size={16}
                  className={`text-muted-foreground transition-transform duration-200 ${mobileAccordion === 'how-it-works' ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileAccordion === 'how-it-works' && (
                <div className="pb-4 space-y-0.5">
                  {dropdowns[0].items.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={closeMobileMenu}
                      className="block py-2.5 pl-3 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Proof Accordion */}
            <div className="border-b border-border/40">
              <button
                onClick={() => toggleMobileAccordion('proof')}
                className="w-full flex items-center justify-between py-4 text-[11px] uppercase tracking-widest font-semibold text-card-foreground"
              >
                <span>Proof</span>
                <Icon
                  name="ChevronDownIcon"
                  size={16}
                  className={`text-muted-foreground transition-transform duration-200 ${mobileAccordion === 'proof' ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileAccordion === 'proof' && (
                <div className="pb-4 space-y-0.5">
                  {dropdowns[1].items.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={closeMobileMenu}
                      className="block py-2.5 pl-3 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Pricing — direct link */}
            <div className="border-b border-border/40">
              <Link
                href="/pricing"
                onClick={closeMobileMenu}
                className="flex items-center py-4 text-[11px] uppercase tracking-widest font-semibold text-card-foreground hover:text-primary transition-colors"
              >
                Pricing
              </Link>
            </div>

            {/* Website Rescue — direct link */}
            <div className="border-b border-border/40">
              <Link
                href="/website-rescue"
                onClick={closeMobileMenu}
                className="flex items-center py-4 text-[11px] uppercase tracking-widest font-semibold text-card-foreground hover:text-primary transition-colors"
              >
                Website Rescue
              </Link>
            </div>

            {/* About Accordion */}
            <div className="border-b border-border/40">
              <button
                onClick={() => toggleMobileAccordion('about')}
                className="w-full flex items-center justify-between py-4 text-[11px] uppercase tracking-widest font-semibold text-card-foreground"
              >
                <span>About</span>
                <Icon
                  name="ChevronDownIcon"
                  size={16}
                  className={`text-muted-foreground transition-transform duration-200 ${mobileAccordion === 'about' ? 'rotate-180' : ''}`}
                />
              </button>
              {mobileAccordion === 'about' && (
                <div className="pb-4 space-y-0.5">
                  {dropdowns[2].items.map((item) => (
                    <Link
                      key={item.href}
                      href={item.href}
                      onClick={closeMobileMenu}
                      className="block py-2.5 pl-3 text-[13px] text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            {/* Book a Sprint CTA */}
            <div className="pt-6 pb-2">
              <Link
                href="/book"
                className="block w-full px-6 py-4 bg-primary text-primary-foreground text-[11px] uppercase tracking-widest text-center font-semibold hover:bg-primary/90 transition-colors"
                onClick={() => {
                  closeMobileMenu();
                  if (typeof window !== 'undefined' && typeof (window as Window & { gtag?: Function }).gtag === 'function') {
                    (window as Window & { gtag: Function }).gtag('event', 'cta_click', { event_category: 'header_nav', cta_label: 'Book a Sprint', location: 'mobile_menu' });
                  }
                }}
              >
                Book a Sprint
              </Link>
            </div>

          </div>
        </div>
      )}
    </nav>
  );
}